import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaMapMarkerAlt,
  FaCheckCircle,
  FaSnowflake,
  FaUsers,
  FaCarSide,
  FaArrowLeft,
} from "react-icons/fa";
import Button from "../../Components/UI/Button";
import { Card, Badge } from "../../Components/UI/Card";
import { StarDisplay } from "../../Components/Marketplace/StarRating";
import BookingModal from "../../Components/Marketplace/BookingModal";
import {
  getServiceById,
  getPartnerReviews,
  bookService,
  getApiErrorMessage,
} from "../../Components/Marketplace/services/marketplaceApi";
import { openMarketplaceCheckout } from "../../Components/Marketplace/services/marketplaceCheckout";
import { formatINR, TRIP_TYPE_LABELS } from "../../Components/Marketplace/marketplaceUtils";

const PLACEHOLDER_IMG =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='800' height='480'><rect width='100%' height='100%' fill='%23f6f7f9'/><text x='50%' y='50%' font-family='sans-serif' font-size='20' fill='%238993a4' text-anchor='middle' dominant-baseline='middle'>Bachatwali Ticket</text></svg>`
  );

const ServiceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = location.state?.prefill || null;

  const [service, setService] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeImg, setActiveImg] = useState(0);

  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [modalError, setModalError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getServiceById(id);
      setService(res?.data || null);

      const partnerId = res?.data?.partnerId?._id;
      if (partnerId) {
        try {
          const rRes = await getPartnerReviews(partnerId);
          setReviews(Array.isArray(rRes?.data) ? rRes.data : []);
        } catch {
          setReviews([]);
        }
      }
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to load this service."));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
    window.scrollTo(0, 0);
  }, [load]);

  const handleBookNowClick = () => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.info("Please login to book this service.");
      navigate("/login/customer");
      return;
    }
    setModalError("");
    setShowModal(true);
  };

  const handleSubmitBooking = async (payload) => {
    setSubmitting(true);
    setModalError("");
    try {
      const res = await bookService(id, payload);
      const { booking, order } = res?.data || {};

      await openMarketplaceCheckout({
        order,
        description: service?.title,
        onSuccess: () => {
          toast.success("Payment successful! Your booking is confirmed.");
          setShowModal(false);
          setSubmitting(false);
          navigate("/dashboard?tab=bookings&sub=Marketplace");
        },
        onError: (err) => {
          setSubmitting(false);
          toast.error(getApiErrorMessage(err, "Payment could not be verified. Please contact support."));
        },
        onDismiss: () => {
          setSubmitting(false);
        },
      });

      if (!order?.id) {
        // Defensive: booking created without a payable order — still surface the state.
        setSubmitting(false);
      }
      void booking;
    } catch (err) {
      setSubmitting(false);
      setModalError(getApiErrorMessage(err, "Could not create this booking. Please try again."));
    }
  };

  if (loading) {
    return (
      <div className="max-w-[1100px] mx-auto px-4 py-16 text-center text-ink-500">
        Loading service...
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="max-w-[1100px] mx-auto px-4 py-16 text-center">
        <p className="text-brand-600 font-semibold mb-3">{error || "Service not found."}</p>
        <Link to="/marketplace" className="text-brand-600 font-semibold">
          ← Back to Marketplace
        </Link>
      </div>
    );
  }

  const photos = (service.photos || []).length ? service.photos : [PLACEHOLDER_IMG];
  const partner = service.partnerId || {};
  const taxi = service.taxiDetails;

  return (
    <div className="min-h-screen bg-ink-50/40">
      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-6">
        <button
          onClick={() => navigate("/marketplace")}
          className="inline-flex items-center gap-2 text-sm font-semibold text-ink-600 hover:text-brand-600 mb-4"
        >
          <FaArrowLeft size={12} /> Back to Marketplace
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            {/* Gallery */}
            <Card className="overflow-hidden mb-6">
              <img
                src={photos[activeImg] || PLACEHOLDER_IMG}
                alt={service.title}
                className="w-full h-72 sm:h-96 object-cover"
                onError={(e) => (e.currentTarget.src = PLACEHOLDER_IMG)}
              />
              {photos.length > 1 && (
                <div className="flex gap-2 p-3 overflow-x-auto">
                  {photos.map((p, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveImg(i)}
                      className={`shrink-0 h-16 w-24 rounded-lg overflow-hidden border-2 ${
                        i === activeImg ? "border-brand-600" : "border-transparent"
                      }`}
                    >
                      <img src={p} alt="" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </Card>

            <div className="mb-2 flex flex-wrap items-center gap-2">
              <Badge tone="dark">{service.category}</Badge>
              <Badge tone={service.availability ? "success" : "warning"}>
                {service.availability ? "Available" : "Currently unavailable"}
              </Badge>
            </div>
            <h1 className="text-2xl font-extrabold text-ink-900 mb-2">{service.title}</h1>
            {!!(service.destinationCities || []).length && (
              <p className="flex items-center gap-1.5 text-ink-500 text-sm mb-4">
                <FaMapMarkerAlt /> {service.destinationCities.join(", ")}
              </p>
            )}
            <p className="text-ink-700 leading-relaxed whitespace-pre-line mb-6">
              {service.description}
            </p>

            {taxi && (
              <Card className="p-5 mb-6">
                <h2 className="font-bold text-ink-900 mb-4">Vehicle details</h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm mb-4">
                  <div className="flex items-center gap-2 text-ink-700">
                    <FaCarSide className="text-brand-600" />
                    <span>{taxi.vehicleType || "—"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-ink-700">
                    <FaUsers className="text-brand-600" />
                    <span>{taxi.seatingCapacity ? `${taxi.seatingCapacity} seats` : "—"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-ink-700">
                    <FaSnowflake className="text-brand-600" />
                    <span>{taxi.isAC ? "AC" : "Non-AC"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-ink-700 truncate">
                    <FaMapMarkerAlt className="text-brand-600 shrink-0" />
                    <span className="truncate">{taxi.serviceArea || "—"}</span>
                  </div>
                  {!!taxi.luggageCapacity && (
                    <div className="flex items-center gap-2 text-ink-700">
                      <span>🧳</span>
                      <span>{taxi.luggageCapacity} bags</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-ink-700">
                    <span>{taxi.driverIncluded ? "✅" : "—"}</span>
                    <span>{taxi.driverIncluded ? "Driver included" : "Self-drive"}</span>
                  </div>
                </div>
                {taxi.vehicleModel && (
                  <p className="text-sm text-ink-500 mb-3">Model: {taxi.vehicleModel}</p>
                )}
                {!!(taxi.tripTypes || []).length && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {taxi.tripTypes.map((t) => (
                      <Badge key={t} tone="brand">
                        {TRIP_TYPE_LABELS[t] || t}
                      </Badge>
                    ))}
                  </div>
                )}
                {(taxi.perKmPrice > 0 || taxi.perHourPrice > 0 || taxi.extraKmCharge > 0) && (
                  <div className="text-xs text-ink-500 border-t border-ink-100 pt-3 space-y-1">
                    {taxi.perKmPrice > 0 && <p>{formatINR(taxi.perKmPrice)} / km</p>}
                    {taxi.perHourPrice > 0 && <p>{formatINR(taxi.perHourPrice)} / hour</p>}
                    {taxi.extraKmCharge > 0 && (
                      <p>
                        {taxi.includedKm || 0} km included, then {formatINR(taxi.extraKmCharge)} / extra km
                      </p>
                    )}
                  </div>
                )}
              </Card>
            )}

            {/* Reviews */}
            <Card className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-bold text-ink-900">Reviews</h2>
                {partner.ratingCount > 0 && (
                  <StarDisplay value={partner.ratingAvg} count={partner.ratingCount} />
                )}
              </div>
              {reviews.length === 0 ? (
                <p className="text-sm text-ink-500">No reviews yet for this partner.</p>
              ) : (
                <div className="space-y-4">
                  {reviews.map((r, i) => (
                    <div key={r._id || i} className="pb-4 border-b border-ink-100 last:border-0 last:pb-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-semibold text-ink-800 text-sm">
                          {r.customerName || r.userName || "Bachatwali Ticket customer"}
                        </span>
                        <StarDisplay value={r.rating} size={12} />
                      </div>
                      <p className="text-sm text-ink-600">{r.text || r.comment}</p>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          {/* Sidebar: partner + booking */}
          <div>
            <Card className="p-5 sticky top-24">
              <p className="text-xs text-ink-400 uppercase font-semibold tracking-wide mb-1">
                Starting at
              </p>
              <p className="text-3xl font-extrabold text-brand-600 mb-4">
                {formatINR(service.basePrice)}
              </p>

              <Button
                variant="primary"
                size="lg"
                className="w-full mb-5"
                disabled={!service.availability}
                onClick={handleBookNowClick}
              >
                {service.availability ? "Book Now" : "Currently Unavailable"}
              </Button>

              <div className="pt-4 border-t border-ink-100">
                <h3 className="text-sm font-bold text-ink-900 mb-2">Operated by</h3>
                <p className="font-semibold text-ink-800">{partner.businessName || "Bachatwali Ticket Partner"}</p>
                {partner.city && <p className="text-sm text-ink-500 mb-1">{partner.city}</p>}
                {partner.ratingCount > 0 && (
                  <div className="mb-2">
                    <StarDisplay value={partner.ratingAvg} count={partner.ratingCount} />
                  </div>
                )}
                {partner.verificationStatus === "Verified" && (
                  <p className="flex items-center gap-1.5 text-sm text-emerald-600 font-semibold">
                    <FaCheckCircle /> Verified partner
                  </p>
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>

      <BookingModal
        show={showModal}
        onClose={() => !submitting && setShowModal(false)}
        mode="service"
        item={service}
        price={service.basePrice}
        prefill={prefill}
        submitting={submitting}
        error={modalError}
        onSubmit={handleSubmitBooking}
      />
    </div>
  );
};

export default ServiceDetailPage;
